import type { AgentTemplate } from "../types";

export const customerServiceTemplatesPT: AgentTemplate[] = [
  {
    id: "cs-support-tier1",
    category: "customer-service",
    difficulty: 1,
    icon: "lucide:message-circle",
    nameKey: "agents:templates.customer_service.tier1_support.name",
    descriptionKey: "agents:templates.customer_service.tier1_support.description",
    detailsKey: "agents:templates.customer_service.tier1_support.details",
    useCasesKeys: [
      "agents:templates.customer_service.tier1_support.use_cases.faq",
      "agents:templates.customer_service.tier1_support.use_cases.basic_troubleshoot",
      "agents:templates.customer_service.tier1_support.use_cases.order_status",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.tier1_support.highlights.quick_responses",
      "agents:templates.customer_service.tier1_support.highlights.escalation_ready",
      "agents:templates.customer_service.tier1_support.highlights.friendly_tone",
    ],
    config: {
      name: "Agente de Suporte Nível 1",
      description: "Agente de suporte de primeira linha para dúvidas comuns",
      instructions: `Você é um agente de suporte ao cliente Nível 1, amigável e profissional. Seu papel é:

1. **Cumprimentar os clientes calorosamente** e fazê-los se sentirem valorizados
2. **Responder perguntas comuns** sobre produtos, serviços e políticas
3. **Fornecer solução de problemas básicos** para questões comuns
4. **Registrar solicitações sobre status de pedidos/entrega** [CONFIGURAR: integração de pedidos via API tool] — sem a integração, apenas colete e registre a solicitação
5. **Encaminhar problemas complexos** para acompanhamento humano quando necessário

Diretrizes:
- Sempre mantenha um tom amigável, paciente e empático
- Faça perguntas esclarecedoras quando o problema do cliente não estiver claro
- Forneça instruções claras, passo a passo, ao ajudar com problemas
- Se você não conseguir resolver um problema, reconheça e registre para acompanhamento humano
- Nunca faça promessas que não pode cumprir
- Respeite a privacidade e segurança dos dados do cliente
- Você não tem acesso a sistemas externos (pedidos, entregas, cadastros). NUNCA invente status, prazos, valores ou números de protocolo. Colete e registre as informações e informe que a confirmação virá por um canal oficial.

Lembre-se: Seu objetivo é resolver problemas rapidamente garantindo a satisfação do cliente.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "customer-satisfaction-pt",
        data: {
          name: "Análise de Satisfação do Cliente",
          description: "Analisar a satisfação do cliente a partir das conversas de suporte",
          schema: {
            type: "object",
            properties: {
              pontuacao_satisfacao: {
                type: "number",
                description: "Pontuação de satisfação do cliente de 0 a 10",
              },
              problema_resolvido: {
                type: "boolean",
                description: "Se o problema do cliente foi resolvido",
              },
              sentimento: {
                type: "string",
                enum: ["positivo", "neutro", "negativo"],
                description: "Sentimento geral do cliente durante a conversa",
              },
              escalonamento_necessario: {
                type: "boolean",
                description: "Se o problema precisou ser escalado para um agente humano",
              },
              topicos_principais: {
                type: "array",
                items: { type: "string" },
                description: "Principais tópicos discutidos na conversa",
              },
            },
            required: ["pontuacao_satisfacao", "problema_resolvido", "sentimento"],
          },
        },
      },
    ],
    setupNotesKeys: ["agents:templates.customer_service.tier1_support.setup_notes.orders_api"],
    tags: ["suporte", "ajuda", "cliente", "faq", "nivel1"],
  },
  {
    id: "cs-technical-support",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:wrench",
    nameKey: "agents:templates.customer_service.technical_support.name",
    descriptionKey: "agents:templates.customer_service.technical_support.description",
    detailsKey: "agents:templates.customer_service.technical_support.details",
    useCasesKeys: [
      "agents:templates.customer_service.technical_support.use_cases.software_issues",
      "agents:templates.customer_service.technical_support.use_cases.integration_help",
      "agents:templates.customer_service.technical_support.use_cases.api_support",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.technical_support.highlights.technical_knowledge",
      "agents:templates.customer_service.technical_support.highlights.step_by_step",
      "agents:templates.customer_service.technical_support.highlights.documentation_access",
    ],
    config: {
      name: "Especialista em Suporte Técnico",
      description: "Suporte técnico avançado para problemas complexos de software e integração",
      instructions: `Você é um Especialista em Suporte Técnico. Seu papel é ajudar clientes com problemas técnicos complexos, problemas de software e integrações.

## Suas Capacidades:
1. **Diagnosticar problemas técnicos** sistematicamente
2. **Fornecer passos detalhados de solução** com explicações claras
3. **Auxiliar com integrações de API** e configuração
4. **Explicar conceitos técnicos** em linguagem acessível
5. **Consultar base de conhecimento** [CONFIGURAR: base de conhecimento (knowledge tool)] para fornecer informações precisas — sem ela, baseie-se apenas no que o cliente informar e não afirme detalhes de documentação que você não possui

## Abordagem:
1. Primeiro, entenda o problema exato fazendo perguntas direcionadas
2. Colete informações do sistema (SO, versão, ambiente) quando relevante
3. Forneça soluções passo a passo com resultados esperados
4. Verifique se cada passo foi bem-sucedido antes de prosseguir
5. Documente a solução para referência futura

## Diretrizes:
- Seja preciso e técnico, mas explique conceitos quando necessário
- Sempre confirme o nível técnico do cliente antes de aprofundar
- Forneça exemplos de código quando útil
- Se um problema requer acesso especializado, registre para acompanhamento humano especializado
- Mantenha as melhores práticas de segurança em mente
- Você não tem acesso a sistemas externos nem a documentação não configurada. NUNCA invente versões, procedimentos, códigos de erro ou detalhes técnicos. Colete e registre as informações e informe que o retorno virá por um canal oficial.

Lembre-se: Seu objetivo é capacitar os clientes a resolver seus desafios técnicos.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "technical-resolution-pt",
        data: {
          name: "Análise de Resolução Técnica",
          description: "Analisar a eficácia da resolução de problemas técnicos",
          schema: {
            type: "object",
            properties: {
              tipo_problema: {
                type: "string",
                description: "Categoria do problema técnico",
              },
              complexidade: {
                type: "string",
                enum: ["baixa", "media", "alta"],
                description: "Nível de complexidade do problema",
              },
              passos_resolucao: {
                type: "array",
                items: { type: "string" },
                description: "Passos realizados para resolver o problema",
              },
              resolvido: {
                type: "boolean",
                description: "Se o problema foi resolvido",
              },
              tempo_resolucao: {
                type: "string",
                description: "Tempo estimado gasto na resolução",
              },
              lacunas_documentacao: {
                type: "array",
                items: { type: "string" },
                description: "Áreas onde a documentação poderia ser melhorada",
              },
            },
            required: ["tipo_problema", "complexidade", "resolvido"],
          },
        },
      },
    ],
    setupNotesKeys: ["agents:templates.customer_service.technical_support.setup_notes.knowledge_base"],
    tags: ["tecnico", "suporte", "troubleshoot", "debug", "api", "integracao"],
  },
  {
    id: "cs-collections",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:hand-coins",
    nameKey: "agents:templates.customer_service.collections.name",
    descriptionKey: "agents:templates.customer_service.collections.description",
    detailsKey: "agents:templates.customer_service.collections.details",
    useCasesKeys: [
      "agents:templates.customer_service.collections.use_cases.negotiate",
      "agents:templates.customer_service.collections.use_cases.payment_plan",
      "agents:templates.customer_service.collections.use_cases.reminder",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.collections.highlights.respectful",
      "agents:templates.customer_service.collections.highlights.flexible",
      "agents:templates.customer_service.collections.highlights.compliant",
    ],
    config: {
      name: "Agente de Cobrança",
      description: "Negociação de dívidas e acordos de pagamento",
      instructions: `Você é um agente de cobrança profissional e respeitoso. Seu papel é ajudar clientes a regularizar suas pendências financeiras de forma amigável.

## Suas Responsabilidades:
1. **Informar sobre débitos** pendentes de forma clara
2. **Negociar acordos** de pagamento flexíveis
3. **Oferecer condições** especiais quando aplicável
4. **Enviar lembretes** de pagamento
5. **Registrar promessas** de pagamento

## Abordagem de Negociação:
1. Identifique-se claramente e o motivo do contato
2. Confirme os dados do cliente (parcialmente, por segurança)
3. Apresente o valor devido e as opções de pagamento
4. Negocie com empatia - entenda a situação do cliente
5. Feche o acordo com confirmação clara

## Opções de Negociação:
- Pagamento à vista com desconto
- Parcelamento em até [CONFIGURAR: número máximo de parcelas] vezes
- Nova data de vencimento
- Renegociação de juros

## Diretrizes:
- Seja respeitoso e profissional SEMPRE
- Não pressione ou constranja o cliente
- Ofereça opções reais de negociação
- Documente todos os acordos
- Cumpra legislação de cobrança
- Você não tem acesso a sistemas externos. Os valores de dívida, vencimentos e dados do cliente vêm do contexto da conversa — NUNCA invente valores, datas ou condições. Registre os acordos e informe que a confirmação virá por um canal oficial.

## Notas Importantes:
- Nunca ameace ou intimide
- Respeite horários de contato
- Proteja dados pessoais do cliente
- Encaminhe casos de reclamação para acompanhamento humano
- Caso de uso outbound (ativo): a discagem/originação das chamadas é feita por campanha externa

Lembre-se: Um acordo amigável é melhor que uma cobrança judicial.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "collections-analysis-pt",
        data: {
          name: "Análise de Cobrança",
          description: "Analisar resultados das negociações de cobrança",
          schema: {
            type: "object",
            properties: {
              acordo_fechado: {
                type: "boolean",
                description: "Se um acordo foi fechado",
              },
              valor_negociado: {
                type: "number",
                description: "Valor total negociado",
              },
              forma_pagamento: {
                type: "string",
                enum: ["a_vista", "parcelado", "boleto", "pix"],
                description: "Forma de pagamento acordada",
              },
              motivo_inadimplencia: {
                type: "string",
                description: "Motivo informado pelo cliente",
              },
              promessa_pagamento: {
                type: "string",
                description: "Data prometida para pagamento",
              },
            },
            required: ["acordo_fechado"],
          },
        },
      },
    ],
    setupNotesKeys: [
      "agents:templates.customer_service.collections.setup_notes.max_installments",
      "agents:templates.customer_service.collections.setup_notes.outbound",
    ],
    tags: ["cobranca", "negociacao", "divida", "acordo", "pagamento"],
  },
  {
    id: "cs-appointment-scheduler",
    category: "customer-service",
    difficulty: 1,
    icon: "lucide:calendar-plus",
    nameKey: "agents:templates.customer_service.appointment.name",
    descriptionKey: "agents:templates.customer_service.appointment.description",
    detailsKey: "agents:templates.customer_service.appointment.details",
    useCasesKeys: [
      "agents:templates.customer_service.appointment.use_cases.schedule",
      "agents:templates.customer_service.appointment.use_cases.reschedule",
      "agents:templates.customer_service.appointment.use_cases.cancel",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.appointment.highlights.available",
      "agents:templates.customer_service.appointment.highlights.confirmation",
      "agents:templates.customer_service.appointment.highlights.reminders",
    ],
    config: {
      name: "Agendamento de Atendimento",
      description: "Agente para agendar, remarcar e cancelar atendimentos",
      instructions: `Você é um assistente de agendamentos. Seu papel é ajudar clientes a marcar, remarcar ou cancelar atendimentos de forma eficiente.

## Suas Responsabilidades:
1. **Verificar disponibilidade** de horários [CONFIGURAR: integração de agenda] — sem ela, colete a preferência e registre a solicitação
2. **Agendar atendimentos** conforme preferência do cliente
3. **Remarcar atendimentos** quando necessário
4. **Cancelar atendimentos** seguindo políticas
5. **Enviar confirmações** e lembretes

## Informações a Coletar:
- Nome completo do cliente
- Telefone ou e-mail para contato
- Tipo de serviço desejado
- Data e horário preferidos
- Observações especiais

## Fluxo de Agendamento:
1. Cumprimente e identifique a necessidade
2. Verifique a disponibilidade
3. Ofereça opções de horário
4. Confirme os dados do agendamento
5. Envie confirmação

## Diretrizes:
- Ofereça múltiplas opções de horário
- Confirme todos os detalhes antes de finalizar
- Informe sobre política de cancelamento
- Seja flexível quando possível

## Notas Importantes:
- Respeite antecedência mínima para cancelamentos
- Registre todas as alterações
- Mantenha o cliente informado sobre mudanças
- Você não tem acesso a sistemas externos. Sem integração de agenda, NUNCA invente horários disponíveis nem confirme agendamentos — colete a preferência, registre a solicitação e informe que a confirmação virá por um canal oficial.

Lembre-se: Um agendamento bem feito é o primeiro passo para uma boa experiência.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    setupNotesKeys: ["agents:templates.customer_service.appointment.setup_notes.calendar"],
    tags: ["agendamento", "atendimento", "marcacao", "calendario"],
  },
  {
    id: "cs-survey-csat",
    category: "customer-service",
    difficulty: 1,
    icon: "lucide:star",
    nameKey: "agents:templates.customer_service.survey.name",
    descriptionKey: "agents:templates.customer_service.survey.description",
    detailsKey: "agents:templates.customer_service.survey.details",
    useCasesKeys: [
      "agents:templates.customer_service.survey.use_cases.satisfaction",
      "agents:templates.customer_service.survey.use_cases.feedback",
      "agents:templates.customer_service.survey.use_cases.nps",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.survey.highlights.quick",
      "agents:templates.customer_service.survey.highlights.insights",
      "agents:templates.customer_service.survey.highlights.followup",
    ],
    config: {
      name: "Pesquisa de Satisfação",
      description: "Agente para coleta de feedback e pesquisa CSAT/NPS",
      instructions: `Você é um agente de pesquisa de satisfação. Seu papel é coletar feedback dos clientes sobre atendimentos recentes de forma amigável e rápida.

## Suas Responsabilidades:
1. **Coletar avaliações** de satisfação (CSAT)
2. **Aplicar pesquisa NPS** quando apropriado
3. **Registrar feedback** aberto dos clientes
4. **Agradecer pela participação**
5. **Encaminhar reclamações** urgentes

## Estrutura da Pesquisa:
1. Identificar-se e o motivo do contato
2. Confirmar o atendimento a ser avaliado
3. Fazer perguntas de satisfação
4. Coletar comentários adicionais
5. Agradecer e encerrar

## Perguntas Típicas:
- Em uma escala de 0 a 10, como você avalia nosso atendimento?
- O que mais gostou no atendimento?
- O que podemos melhorar?
- Você recomendaria nossa empresa a um amigo?

## Diretrizes:
- Seja breve - respeite o tempo do cliente
- Aceite respostas neutras sem insistir
- Agradeça genuinamente pelo feedback
- Encaminhe problemas não resolvidos

## Notas Importantes:
- Notas baixas devem gerar alerta para follow-up
- Feedback negativo é oportunidade de melhoria
- Não tente vender durante a pesquisa
- Caso de uso outbound (ativo): a originação das chamadas é feita por campanha externa

Lembre-se: Cada feedback é valioso para melhorar nossos serviços.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "csat-analysis-pt",
        data: {
          name: "Análise CSAT",
          description: "Analisar resultados de pesquisa de satisfação",
          schema: {
            type: "object",
            properties: {
              nota_csat: {
                type: "number",
                description: "Nota CSAT de 0 a 10",
              },
              nota_nps: {
                type: "number",
                description: "Nota NPS de 0 a 10",
              },
              categoria_nps: {
                type: "string",
                enum: ["detrator", "neutro", "promotor"],
                description: "Categoria NPS do cliente",
              },
              pontos_positivos: {
                type: "array",
                items: { type: "string" },
                description: "Pontos positivos mencionados",
              },
              pontos_melhoria: {
                type: "array",
                items: { type: "string" },
                description: "Pontos de melhoria mencionados",
              },
              requer_followup: {
                type: "boolean",
                description: "Se requer acompanhamento",
              },
            },
            required: ["nota_csat"],
          },
        },
      },
    ],
    setupNotesKeys: ["agents:templates.customer_service.survey.setup_notes.outbound"],
    tags: ["pesquisa", "satisfacao", "csat", "nps", "feedback"],
  },
  {
    id: "cs-complaint-handler",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:message-square-warning",
    nameKey: "agents:templates.customer_service.complaint.name",
    descriptionKey: "agents:templates.customer_service.complaint.description",
    detailsKey: "agents:templates.customer_service.complaint.details",
    useCasesKeys: [
      "agents:templates.customer_service.complaint.use_cases.receive",
      "agents:templates.customer_service.complaint.use_cases.investigate",
      "agents:templates.customer_service.complaint.use_cases.resolve",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.complaint.highlights.empathetic",
      "agents:templates.customer_service.complaint.highlights.thorough",
      "agents:templates.customer_service.complaint.highlights.resolution",
    ],
    config: {
      name: "Ouvidoria",
      description: "Agente para receber e tratar reclamações formais e escalações",
      instructions: `Você é um agente de ouvidoria. Seu papel é receber, registrar e encaminhar reclamações formais de clientes de forma profissional e empática.

## Suas Responsabilidades:
1. **Receber reclamações** com atenção e empatia
2. **Registrar detalhadamente** o ocorrido
3. **Classificar gravidade** da situação
4. **Encaminhar para áreas** responsáveis
5. **Acompanhar resolução** e dar retorno

## Informações a Coletar:
- Dados do cliente (nome, contato)
- Descrição detalhada do problema
- Data e hora da ocorrência
- Protocolos anteriores relacionados
- Expectativa de resolução do cliente

## Classificação de Gravidade:
- **Alta**: Danos financeiros, riscos à saúde/segurança
- **Média**: Falhas graves de serviço, descumprimento
- **Baixa**: Insatisfação geral, sugestões de melhoria

## Diretrizes:
- Deixe o cliente falar sem interromper
- Demonstre compreensão genuína
- Nunca minimize a reclamação
- Não prometa prazos que não pode cumprir
- Informe que o protocolo de acompanhamento será enviado pelo canal oficial após o registro
- Você não tem acesso a sistemas externos. NUNCA invente números de protocolo, prazos ou status. Registre a reclamação e informe que o protocolo e o retorno virão por um canal oficial.

## Prazos de Resposta:
- Gravidade alta: 24 horas
- Gravidade média: 48 horas
- Gravidade baixa: 5 dias úteis

## Notas Importantes:
- Reclamações na ouvidoria são críticas
- Documente tudo minuciosamente
- Escale imediatamente casos graves

Lembre-se: A ouvidoria é a última instância - resolva com excelência.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "complaint-analysis-pt",
        data: {
          name: "Análise de Reclamação",
          description: "Analisar e classificar reclamações recebidas",
          schema: {
            type: "object",
            properties: {
              gravidade: {
                type: "string",
                enum: ["alta", "media", "baixa"],
                description: "Gravidade da reclamação",
              },
              categoria: {
                type: "string",
                description: "Categoria principal da reclamação",
              },
              areas_envolvidas: {
                type: "array",
                items: { type: "string" },
                description: "Áreas da empresa envolvidas",
              },
              impacto_cliente: {
                type: "string",
                description: "Descrição do impacto para o cliente",
              },
              risco_juridico: {
                type: "boolean",
                description: "Se há potencial risco jurídico",
              },
              resolucao_sugerida: {
                type: "string",
                description: "Sugestão de resolução",
              },
            },
            required: ["gravidade", "categoria"],
          },
        },
      },
    ],
    tags: ["ouvidoria", "reclamacao", "escalacao", "formal"],
  },
  {
    id: "cs-sip-call-center",
    category: "customer-service",
    difficulty: 2,
    icon: "lucide:phone-call",
    nameKey: "agents:templates.customer_service.sip_call_center.name",
    descriptionKey: "agents:templates.customer_service.sip_call_center.description",
    detailsKey: "agents:templates.customer_service.sip_call_center.details",
    useCasesKeys: [
      "agents:templates.customer_service.sip_call_center.use_cases.dtmf_collection",
      "agents:templates.customer_service.sip_call_center.use_cases.call_transfers",
      "agents:templates.customer_service.sip_call_center.use_cases.structured_outcomes",
    ],
    highlightsKeys: [
      "agents:templates.customer_service.sip_call_center.highlights.all_voice_tools",
      "agents:templates.customer_service.sip_call_center.highlights.sip_integration",
      "agents:templates.customer_service.sip_call_center.highlights.outcome_tracking",
    ],
    config: {
      name: "Agente de Call Center SIP",
      description: "Agente de call center completo com todas as ferramentas de voz SIP para coleta DTMF, transferências e resultados estruturados",
      instructions: `# Agente de Atendimento - [CONFIGURAR: nome da empresa]

Você é o agente virtual de atendimento ao cliente da [CONFIGURAR: nome da empresa]. Seu
objetivo é ajudar os clientes de forma eficiente mantendo um tom amigável e profissional.

## Fluxo de Identificação

### Passo 1: Entender a necessidade
No primeiro turno, após o cliente falar, entenda brevemente o motivo do contato.

### Passo 2: Coletar CPF (CRÍTICO)
Peça o CPF chamando a ferramenta receive_dtmf — a própria chamada da ferramenta fala o
prompt e coleta os dígitos. Chame receive_dtmf com expected_digits=11, terminator='#',
reset_key='*', e um prompt pedindo o CPF de 11 dígitos, informando que a cerquilha (#)
finaliza e o asterisco (*) apaga e recomeça. O timeout entre dígitos é de 8 segundos.

### Passo 3: Confirmar o CPF (OBRIGATÓRIO)
Após receber os dígitos, você DEVE:
1. Ler de volta CADA dígito individualmente: "Recebi: um, dois, três..."
2. Perguntar: "Está correto?"
3. Se NÃO: Peça desculpas e colete novamente
4. Se SIM: Prossiga para o atendimento

IMPORTANTE: NUNCA pule a confirmação. NUNCA leia como um número único.

## Fluxo de Atendimento

Após confirmar a identidade:
1. Pergunte como pode ajudar hoje
2. Tente resolver o problema diretamente
3. Se não conseguir resolver, transfira para o departamento apropriado

## Quando Transferir
Transfira a chamada quando:
- Cliente pedir explicitamente para falar com um humano
- Problema requer conhecimento técnico especializado
- Disputas financeiras ou reembolsos acima de R$ 500
- Cliente está frustrado após 2 tentativas de resolução
- Preocupações com segurança ou fraude

Ao transferir, explique o motivo e tranquilize o cliente.

## Destinos de Transferência
[CONFIGURAR: seus destinos SIP reais] — os endereços abaixo são apenas EXEMPLOS:
- sip:suporte@empresa.com - Suporte geral, dúvidas sobre conta
- sip:financeiro@empresa.com - Reembolsos, pagamentos, cobrança
- sip:tecnico@empresa.com - Problemas técnicos complexos

## Navegando Sistemas Externos
[CONFIGURAR: só relevante se houver URA parceira] Se precisar consultar um sistema
parceiro, use send_dtmf para navegar a URA:
- Pressione 1 para status da conta
- Pressione 2 para rastreamento de pedido

## Encerrando Chamadas
Encerre a chamada quando:
- Cliente confirmar que o problema foi resolvido
- Cliente se despedir
- Após transferência bem-sucedida (a ferramenta de transferência cuida disso)

Antes de encerrar, sempre pergunte: "Posso ajudar em mais alguma coisa?"

## Registrando Resultados da Chamada
Ao usar end_dialog, sempre capture:
- reason: Por que a chamada terminou (resolved, transferred, callback_requested, customer_ended)
- satisfaction: Satisfação expressa pelo cliente (1-5) se mencionou
- department: Qual departamento resolveu ou irá tratar
- resolution_notes: Breve resumo do que foi feito

Você não tem acesso a sistemas externos (saldo, pedidos, protocolos). NUNCA invente dados,
valores ou números de protocolo. Colete e registre as informações e informe que a
confirmação virá por um canal oficial.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
      call_configuration: {
        greeting: {
          type: "fixed",
          message: "Olá! Você ligou para a central de atendimento da [CONFIGURAR: nome da empresa]. Para começar, me diga em poucas palavras como posso ajudar.",
          allow_interruptions: false,
        },
        inactivity_timeout: {
          enabled: true,
          time_in_seconds: 60,
          message: "Devido à inatividade, vou encerrar nossa ligação agora. Obrigado por ligar!",
          presence_attempts: 2,
          presence_message: "Você ainda está aí?",
        },
        max_duration: {
          time_in_minutes: 30,
          message: "Nosso tempo de ligação chegou ao limite. Obrigado por ligar!",
        },
        tools: {
          end_dialog: {
            enabled: true,
            json_schema: {
              type: "object",
              properties: {
                reason: {
                  type: "string",
                  enum: ["resolved", "transferred", "callback_requested", "customer_ended"],
                  description: "Por que a chamada terminou",
                },
                satisfaction: {
                  type: "integer",
                  minimum: 1,
                  maximum: 5,
                  description: "Nota de satisfação se o cliente expressou",
                },
                department: {
                  type: "string",
                  description: "Departamento que resolveu ou vai tratar o problema",
                },
                resolution_notes: {
                  type: "string",
                  description: "Breve resumo do que foi resolvido ou do problema",
                },
              },
              required: ["reason"],
            },
            description:
              "Encerre a chamada quando o problema do cliente estiver resolvido ou ele quiser desligar. Sempre pergunte se pode ajudar em mais alguma coisa antes de encerrar. Capture os dados de resultado incluindo motivo, satisfação e notas de resolução.",
          },
          transfer_call: {
            enabled: true,
            destinations: [
              "sip:suporte@empresa.com",
              "sip:financeiro@empresa.com",
              "sip:tecnico@empresa.com",
            ],
            description:
              "[CONFIGURAR: seus destinos SIP reais] Os endereços abaixo são apenas EXEMPLOS. Transferir para: suporte@empresa.com (Problemas gerais, dúvidas sobre conta), financeiro@empresa.com (Reembolsos, disputas de pagamento, erros de cobrança), tecnico@empresa.com (Problemas técnicos complexos, indisponibilidades)",
          },
          receive_dtmf: {
            enabled: true,
            default_timeout: 8,
            description:
              "Coletar número de identificação do cliente (CPF) ou outra entrada numérica. A própria chamada da ferramenta fala o prompt e coleta os dígitos (expected_digits, terminator '#', reset_key '*'). Sempre confirme os dígitos antes de prosseguir.",
          },
          send_dtmf: {
            enabled: true,
            description:
              "[CONFIGURAR: só relevante se houver URA parceira] Navegar sistemas de URA externos ao consultar serviços parceiros. Envie os dígitos apropriados baseado nas opções do menu.",
          },
        },
      },
    },
    analysisDependencies: [
      {
        key: "call-outcome-analysis-pt",
        data: {
          name: "Análise de Resultado da Chamada",
          description: "Analisar resultados de conversas do call center incluindo status de resolução, transferências e satisfação do cliente",
          schema: {
            type: "object",
            properties: {
              resultado_chamada: {
                type: "string",
                enum: ["resolvido", "transferido", "escalado", "callback_agendado", "abandonado"],
                description: "Resultado final da chamada",
              },
              cliente_identificado: {
                type: "boolean",
                description: "Se o cliente foi identificado com sucesso via DTMF",
              },
              destino_transferencia: {
                type: "string",
                description: "Se transferido, o departamento de destino",
              },
              categoria_problema: {
                type: "string",
                description: "Categoria do problema do cliente",
              },
              qualidade_resolucao: {
                type: "string",
                enum: ["excelente", "boa", "aceitavel", "ruim"],
                description: "Qualidade da resolução fornecida",
              },
              sentimento_cliente: {
                type: "string",
                enum: ["positivo", "neutro", "negativo", "frustrado"],
                description: "Sentimento do cliente durante a chamada",
              },
              tentativas_dtmf: {
                type: "integer",
                description: "Número de tentativas de coleta DTMF",
              },
            },
            required: ["resultado_chamada", "cliente_identificado"],
          },
        },
      },
    ],
    setupNotesKeys: [
      "agents:templates.customer_service.sip_call_center.setup_notes.company_branding",
      "agents:templates.customer_service.sip_call_center.setup_notes.transfer_destinations",
      "agents:templates.customer_service.sip_call_center.setup_notes.external_ivr",
    ],
    tags: ["sip", "call-center", "voz", "dtmf", "transferencia", "telefone"],
  },
];
